import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { alpha, styled, useTheme } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import VideocamIcon from "@material-ui/icons/Videocam";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import Label from "components/Label";
import useLocalStorage from "hooks/useLocalStorage";
import {
  ACTIVE_WEBINAR_PROMO,
  isWebinarLive,
  isWebinarPromoActive,
  shouldShowJoinLink,
  WebinarPromoEvent
} from "./webinarPromo.config";
import { useWebinarPromoNow } from "./useWebinarPromoNow";
import { formatWebinarCountdown } from "./webinarPromoCountdown";

/**
 * URL search param the in-page widget appends to force-open this dialog
 * from "More details". Mirrors the GlobalNewSignatureDialog pattern so the
 * trigger doesn't need props or context — anyone on /dashboard/* can open
 * the modal by setting this flag.
 */
export const WEBINAR_PROMO_OPEN_PARAM = "webinarPromo";

interface WebinarPromoDialogProps {
  readonly event?: WebinarPromoEvent;
}

/**
 * Brand-aligned gradient surface. The cropped corner blobs add depth without
 * needing an asset and read nicely in both LTR and RTL.
 */
const RootDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 20,
    overflow: "hidden",
    backgroundImage: `linear-gradient(150deg, ${alpha(
      theme.palette.primary.lighter,
      0.95
    )} 0%, ${theme.palette.background.paper} 55%)`,
    boxShadow: `0 24px 60px ${alpha(theme.palette.primary.main, 0.25)}`
  }
}));

/**
 * Globally-mounted webinar/event promo popup. Auto-opens once per device on
 * any /dashboard/* page (mounted inside DashboardLayout), then persists a
 * per-event dismiss flag in localStorage so it never re-opens for the same
 * event. Bump `storageKey` in the config to release a new event.
 */
export function WebinarPromoDialog({
  event = ACTIVE_WEBINAR_PROMO
}: WebinarPromoDialogProps): JSX.Element | null {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const now = useWebinarPromoNow();

  const [dismissed, setDismissed] = useLocalStorage<boolean>(event.storageKey, false);
  const [autoOpen, setAutoOpen] = useState(false);

  const isOnDashboard = location.pathname.startsWith("/dashboard");
  const isActive = isWebinarPromoActive(event, now);
  const live = isWebinarLive(event, now);
  const joinable = shouldShowJoinLink(event, now);
  const countdown =
    now.getTime() < new Date(event.startsAt).getTime()
      ? formatWebinarCountdown(new Date(event.startsAt).getTime(), now.getTime(), t)
      : null;
  // Manual trigger from the in-page widget — always wins over the dismiss
  // flag so a user who closed the popup can still pull it back up via
  // "More details".
  const manuallyOpen = searchParams.get(WEBINAR_PROMO_OPEN_PARAM) === "1";
  const open = manuallyOpen || autoOpen;

  // Delay opening so users see their dashboard first — feels like a notice,
  // not a wall blocking their work. Manual triggers skip this path entirely.
  useEffect(() => {
    if (!isActive || dismissed || !isOnDashboard || manuallyOpen) return;
    const id = window.setTimeout(() => setAutoOpen(true), event.popupDelayMs);
    return () => window.clearTimeout(id);
  }, [isActive, dismissed, isOnDashboard, manuallyOpen, event.popupDelayMs]);

  if (!isActive || !isOnDashboard) return null;
  // If the user dismissed AND the URL doesn't have the manual flag, render
  // nothing — keeps the auto-open path one-shot per device.
  if (dismissed && !manuallyOpen) return null;

  const close = () => {
    setAutoOpen(false);
    setDismissed(true);
    if (manuallyOpen) {
      const next = new URLSearchParams(searchParams);
      next.delete(WEBINAR_PROMO_OPEN_PARAM);
      setSearchParams(next, { replace: true });
    }
  };

  const handlePrimaryAction = () => {
    // Inside the join window, prefer the meeting URL — calendar links are
    // irrelevant once the event is about to start (or running). Outside the
    // window, fall back to the "Add to calendar" template.
    const targetUrl = joinable ? event.meetingUrl : event.calendarUrl;
    // Open in a new tab so the user doesn't lose their dashboard. Dismiss
    // either way — once they've clicked, this popup shouldn't follow them.
    window.open(targetUrl, "_blank", "noopener,noreferrer");
    close();
  };

  return (
    <RootDialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      aria-labelledby="webinar-promo-title"
    >
      <DialogContent sx={{ p: 0, position: "relative" }}>
        <IconButton
          size="small"
          onClick={close}
          aria-label={t(event.i18n.dismissKey, "Dismiss") as string}
          sx={{
            position: "absolute",
            top: 12,
            insetInlineEnd: 12,
            zIndex: 2,
            backgroundColor: alpha(theme.palette.common.white, 0.7),
            "&:hover": { backgroundColor: theme.palette.common.white }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Hero — brand-green block with the icon. Sized to feel like a
            poster header without dominating the dialog on mobile. */}
        <Box
          sx={{
            position: "relative",
            backgroundImage: (t) =>
              `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
            color: "common.white",
            px: { xs: 3, sm: 5 },
            pt: { xs: 4, sm: 5 },
            pb: { xs: 3, sm: 4 },
            overflow: "hidden"
          }}
        >
          {/* Floating decorative blobs. Each circle drifts on its own
              keyframe + duration + delay so they never sync up, which is
              what sells the "organic floating" feel. Animations are
              disabled under prefers-reduced-motion for accessibility. */}
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              insetInlineEnd: -40,
              top: -40,
              width: 180,
              height: 180,
              borderRadius: "50%",
              backgroundColor: alpha(theme.palette.common.white, 0.12),
              willChange: "transform",
              "@keyframes webinarFloatA": {
                "0%, 100%": { transform: "translate(0, 0) scale(1)" },
                "50%": { transform: "translate(-14px, 18px) scale(1.05)" }
              },
              animation: "webinarFloatA 11s ease-in-out infinite",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" }
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              insetInlineStart: -30,
              bottom: -50,
              width: 140,
              height: 140,
              borderRadius: "50%",
              backgroundColor: alpha(theme.palette.common.white, 0.08),
              willChange: "transform",
              "@keyframes webinarFloatB": {
                "0%, 100%": { transform: "translate(0, 0) scale(1)" },
                "50%": { transform: "translate(18px, -22px) scale(0.95)" }
              },
              animation: "webinarFloatB 14s ease-in-out infinite",
              animationDelay: "-3s",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" }
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              insetInlineStart: "30%",
              top: "55%",
              width: 70,
              height: 70,
              borderRadius: "50%",
              backgroundColor: alpha(theme.palette.common.white, 0.07),
              willChange: "transform",
              "@keyframes webinarFloatC": {
                "0%, 100%": { transform: "translate(0, 0)" },
                "33%": { transform: "translate(10px, -12px)" },
                "66%": { transform: "translate(-8px, 8px)" }
              },
              animation: "webinarFloatC 17s ease-in-out infinite",
              animationDelay: "-6s",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" }
            }}
          />

          <Stack
            direction="row"
            alignItems="center"
            gap={1.5}
            sx={{ mb: 2, position: "relative" }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: alpha(theme.palette.common.white, 0.18)
              }}
            >
              <EventAvailableIcon sx={{ fontSize: 28 }} />
            </Box>
            {live ? (
              <Label
                variant="filled"
                sx={{
                  backgroundColor: "error.main",
                  color: "common.white",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  pl: 0.5,
                  "@keyframes webinarLivePulseDialog": {
                    "0%, 100%": { boxShadow: "0 0 0 0 rgba(255,255,255,0.55)" },
                    "70%": { boxShadow: "0 0 0 10px rgba(255,255,255,0)" }
                  },
                  animation: "webinarLivePulseDialog 1.6s ease-out infinite",
                  "@media (prefers-reduced-motion: reduce)": { animation: "none" }
                }}
              >
                <FiberManualRecordIcon sx={{ fontSize: 10 }} />
                {t(event.i18n.liveBadgeKey, "LIVE NOW")}
              </Label>
            ) : (
              <Label
                variant="filled"
                sx={{
                  backgroundColor: alpha(theme.palette.common.white, 0.22),
                  color: "common.white"
                }}
              >
                {t(event.i18n.badgeKey, "Live webinar")}
              </Label>
            )}
          </Stack>

          <Typography
            id="webinar-promo-title"
            variant={isMobile ? "h4" : "h3"}
            sx={{ position: "relative", color: "common.white", lineHeight: 1.2, mb: 1.5 }}
          >
            {t(event.i18n.titleKey, "Updates webinar — new features")}
          </Typography>

          <Stack direction="row" alignItems="center" gap={1} sx={{ position: "relative" }}>
            <CalendarTodayIcon sx={{ fontSize: 18, opacity: 0.9 }} />
            <Typography variant="subtitle2" sx={{ color: "common.white", opacity: 0.95 }}>
              {t(event.i18n.dateLabelKey, "Tue, May 19 · 13:00–14:00 IL")}
            </Typography>
          </Stack>
          {countdown && (
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ position: "relative", mt: 0.75 }}
            >
              <HourglassEmptyIcon sx={{ fontSize: 18, opacity: 0.9 }} />
              <Typography
                variant="subtitle2"
                sx={{ color: "common.white", opacity: 0.95, fontWeight: 700 }}
              >
                {countdown}
              </Typography>
            </Stack>
          )}
        </Box>

        <Box sx={{ px: { xs: 3, sm: 5 }, py: { xs: 3, sm: 4 } }}>
          <Typography
            variant="body1"
            sx={{
              color: "text.primary",
              mb: 3,
              lineHeight: 1.6,
              // Renders \n in the locale string as actual line breaks so we
              // can keep paragraph spacing inside one translation key.
              whiteSpace: "pre-line"
            }}
          >
            {t(
              event.i18n.descriptionKey,
              "Join us live for a hands-on tour of everything new in Webot. We'll demo, explain, and answer your questions so you can squeeze the most out of every tool."
            )}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} alignItems="stretch">
            <Button
              fullWidth
              variant="contained"
              size="large"
              color={joinable ? "error" : "primary"}
              onClick={handlePrimaryAction}
              startIcon={joinable ? <VideocamIcon /> : <EventAvailableIcon />}
              sx={{
                py: 1.5,
                fontSize: 16,
                boxShadow: (t) =>
                  joinable
                    ? `0 8px 20px ${alpha(t.palette.error.main, 0.4)}`
                    : `0 8px 20px ${alpha(t.palette.primary.main, 0.35)}`
              }}
            >
              {joinable
                ? t(event.i18n.joinNowKey, "Join the meeting")
                : t(event.i18n.widgetCtaKey, "Add to calendar")}
            </Button>
            <Button
              fullWidth={isMobile}
              variant="text"
              size="large"
              onClick={close}
              sx={{ color: "text.secondary", py: 1.5 }}
            >
              {t(event.i18n.dismissKey, "Not now")}
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </RootDialog>
  );
}
